const eventPhase = require("./event-phase");

const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;

function describeCountdown(seconds) {
  if (seconds < SECONDS_PER_MINUTE) {
    return "less than a minute";
  }
  const minutes = Math.round(seconds / SECONDS_PER_MINUTE);
  if (minutes < MINUTES_PER_HOUR) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / MINUTES_PER_HOUR);
  if (hours < HOURS_PER_DAY) {
    const restMinutes = minutes % MINUTES_PER_HOUR;

    return restMinutes ? `${hours} h ${restMinutes} min` : `${hours} h`;
  }
  const days = Math.floor(hours / HOURS_PER_DAY);
  const restHours = hours % HOURS_PER_DAY;

  return restHours ? `${days} d ${restHours} h` : `${days} d`;
}

function toPhaseRow(entity) {
  const pick = (...keys) => keys.map(key => entity?.[key]).find(value => value !== undefined && value !== null) ?? 0;

  return {
    start_timestamp: pick("startTime", "startTimestamp", "start_timestamp"),
    end_timestamp: pick("endTime", "endTimestamp", "end_timestamp"),
    completed_at: pick("completedAt", "completed_at"),
    always_available: pick("alwaysAvailable", "always_available"),
    allow_early_join: pick("allowEarlyJoin", "allow_early_join"),
    early_join_minutes: pick("earlyJoinMinutes", "early_join_minutes"),
    hide_chat_outside_meeting: pick("hideChatOutsideMeeting", "hide_chat_outside_meeting"),
    [eventPhase.KEY_SERIES_COMPLETED_AT]: pick("seriesCompletedAt", "series_completed_at"),
  };
}

const EMPTY_PRESENCE = Object.freeze({ participants: 0, publishers: 0, viewers: 0, completedAt: 0 });

function withPresenceWindow(entity, presence) {
  const presenceOccurrenceStart = Number(presence?.occurrenceStart || 0);
  const entityOccurrenceStart = Number(entity?.occurrenceStart || entity?.occurrence_start || 0);

  if (presenceOccurrenceStart > entityOccurrenceStart) {
    const occurrenceStartTimestamp = Number(presence.startTimestamp || 0);
    const occurrenceEndTimestamp = Number(presence.endTimestamp || 0);

    return {
      ...entity,
      occurrenceStart: presenceOccurrenceStart,
      startTime: occurrenceStartTimestamp,
      endTime: occurrenceEndTimestamp,
      startTimestamp: occurrenceStartTimestamp,
      endTimestamp: occurrenceEndTimestamp,
      completedAt: Number(presence.completedAt || 0),
      seriesCompletedAt: Number(presence.seriesCompletedAt || 0),
    };
  }

  const completedAt = Number(entity?.completedAt || entity?.completed_at || 0) || Number(presence?.completedAt || 0);

  return completedAt ? { ...entity, completedAt } : { ...entity };
}

function isRoomActive(isLivestream, presence) {
  const counts = presence || EMPTY_PRESENCE;

  return isLivestream ? Number(counts.publishers) > 0 : Number(counts.participants) > 0;
}

function describeStatus(state, { isLivestream = false, presence = EMPTY_PRESENCE } = {}) {
  switch (state.phase) {
    case eventPhase.PHASE_LIVE:
      if (isLivestream) {
        if (presence?.broadcastStatus === "paused") {
          return "Back soon";
        }

        return isRoomActive(true, presence) ? "Live" : "Starting soon";
      }

      return isRoomActive(false, presence) ? "In progress" : "No one here yet";
    case eventPhase.PHASE_EARLY:
      return "Starting soon";
    case eventPhase.PHASE_ENDED:
      return "Ended";
    default:
      return state.secondsUntilStart > 0 ? `Starts in ${describeCountdown(state.secondsUntilStart)}` : "";
  }
}

function isEventLive({ entity, now, isLivestream = false, presence = EMPTY_PRESENCE }) {
  const state = eventPhase.resolve(toPhaseRow(withPresenceWindow(entity, presence)), now);

  return state.phase === eventPhase.PHASE_LIVE && isRoomActive(isLivestream, presence);
}

function seriesIsCancelled(row) {
  return Number(row[eventPhase.KEY_SERIES_COMPLETED_AT]) > 0;
}

function joinSubtitle(state) {
  if (state.phase === eventPhase.PHASE_ENDED) {
    return "This meeting has ended. You can join again any time.";
  }

  if (state.phase === eventPhase.PHASE_LIVE) {
    return "Your camera and microphone are only requested once you join.";
  }

  return `Starts in ${describeCountdown(state.secondsUntilStart)}. `
    + "You can join now and wait for the others.";
}

function describeJoinCard({
  entity,
  now,
  canModerate = false,
  isWatchOnly = false,
  isLivestreamHost = false,
  isLivestream = isWatchOnly || isLivestreamHost,
  presence = EMPTY_PRESENCE,
}) {
  const row = toPhaseRow(withPresenceWindow(entity, presence));
  const state = eventPhase.resolve(row, now);
  const title = entity?.title || "";

  const status = describeStatus(state, { isLivestream, presence });

  if (seriesIsCancelled(row) && !canModerate) {
    return {
      canJoin: false,
      title: "These meetings were cancelled",
      subtitle: "The host cancelled the rest of this series.",
      phase: state.phase,
      status,
    };
  }

  if (isWatchOnly) {
    return {
      canJoin: true,
      title: title ? `Watch ${title}` : "Watch the livestream",
      subtitle: "You'll join as a viewer. Your camera and microphone stay off.",
      phase: state.phase,
      status,
    };
  }

  const joinTitle = title || "Join the event";

  if (isLivestreamHost) {
    return {
      canJoin: true,
      title: joinTitle,
      subtitle: "Viewers see the stream as soon as you go live.",
      phase: state.phase,
      status,
    };
  }

  return {
    canJoin: true,
    title: joinTitle,
    subtitle: joinSubtitle(state),
    phase: state.phase,
    status,
  };
}

module.exports = {
  describeJoinCard,
  describeCountdown,
  describeStatus,
  isEventLive,
  toPhaseRow,
  withPresenceWindow,
};
